/**
 * Resolves Evidence country/jurisdiction from team enforcement, then the user's
 * `evidence_settings`, then falls back to the account `User.country` /
 * `User.jurisdiction` (About You).
 *
 * The account fallback keeps region-specific behaviour working for existing
 * users who only set a location in account settings — e.g. the AU regulatory
 * Instructions-For-Use section — until `evidence_settings` is populated (by a
 * backend backfill or the next time they touch Evidence onboarding).
 *
 * The fallback is applied as a coherent pair: jurisdiction is only seeded from
 * the account when the country is too. Once `evidence_settings.country` is set
 * we trust `evidence_settings` for both fields, so we never pair an explicit
 * Evidence country with an unrelated account jurisdiction.
 *
 * When `evidence_settings.country` is {@link INTERNATIONAL_EVIDENCE_COUNTRY}
 * AND the International option is honoured (flag on, non-med-device region),
 * both fields resolve to `null` and the account fallback is skipped so
 * international clinicians are not pinned to their profile country (EVI-2318).
 * If the flag is off or the Kinde region is medical-device-regulated, the
 * sentinel is treated as unset and the account fallback applies (fail closed).
 */

import { useIsInternationalEvidenceCountryEnabled } from '@/features/flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import {
  INTERNATIONAL_EVIDENCE_COUNTRY,
  honourInternationalEvidenceCountry,
  isInternationalEvidenceCountry,
} from '@heidi/evidence/contracts';
import { useMemo } from 'react';

export { INTERNATIONAL_EVIDENCE_COUNTRY };

export function useEffectiveEvidenceLocation() {
  const isInternationalCountryFlagEnabled = useIsInternationalEvidenceCountryEnabled();
  const { region, isPending, isError } = useIsUKOrEU();
  const honourInternational = honourInternationalEvidenceCountry({
    flagEnabled: isInternationalCountryFlagEnabled,
    region,
    isPending,
    isError,
  });

  const {
    evidenceCountry: teamCountry,
    evidenceJurisdiction: teamJurisdiction,
    teamName,
  } = useTeamEvidenceEnforcement();

  const { data: location } = useQueryUser((data) => ({
    evidenceCountry: data?.evidence_settings?.country ?? null,
    evidenceJurisdiction: data?.evidence_settings?.jurisdiction ?? null,
    accountCountry: data?.country ?? null,
    accountJurisdiction: data?.jurisdiction ?? null,
  }));

  const storedInternational = isInternationalEvidenceCountry(location?.evidenceCountry);
  const isInternationalEvidence = storedInternational && honourInternational;

  // Personal (non-enforced) location, with a coherent account fallback.
  const personal = useMemo(() => {
    if (isInternationalEvidence) {
      return {
        country: null,
        jurisdiction: null,
      };
    }
    if (storedInternational) {
      return {
        country: location?.accountCountry ?? null,
        jurisdiction: location?.accountJurisdiction ?? null,
      };
    }
    if (location?.evidenceCountry) {
      return {
        country: location.evidenceCountry,
        jurisdiction: location.evidenceJurisdiction ?? null,
      };
    }
    return {
      country: location?.accountCountry ?? null,
      jurisdiction: location?.accountJurisdiction ?? null,
    };
  }, [
    isInternationalEvidence,
    storedInternational,
    location?.evidenceCountry,
    location?.evidenceJurisdiction,
    location?.accountCountry,
    location?.accountJurisdiction,
  ]);

  const country = useMemo(
    () => (teamCountry.enforced ? teamCountry.value : personal.country),
    [teamCountry.enforced, teamCountry.value, personal.country]
  );

  const jurisdiction = useMemo(
    () => (teamJurisdiction.enforced ? teamJurisdiction.value : personal.jurisdiction),
    [teamJurisdiction.enforced, teamJurisdiction.value, personal.jurisdiction]
  );

  return {
    country,
    jurisdiction,
    isInternationalEvidence,
    teamCountry,
    teamJurisdiction,
    teamName,
  };
}
